import React, { FC } from 'react';
import { Field } from 'react-final-form';

import cx from 'classnames';

import { Input, Select } from '@nord/ui';

import { Required } from '../../validation/FormValidation';
import FieldError, { isError } from './FieldError';
import { IInputWrapProps } from './interfaces';

/**
 * It is prohibited to use import aliases from @nord/ui package,
 * as we've excluded this dependency from vite optimizations in CP.
 * If `as` in `import { Input as SomeInput, Select } from '@nord/ui';` syntax is used,
 * then `SomeInput` variable won't be available after performing optimizations to create a production-ready build.
 */
const InputComponent = Input;
const SelectComponent = Select;

const InputWrap: FC<IInputWrapProps> = ({
  name,
  type = 'text',
  label,
  labelComponent: LabelComponent,
  required = false,
  inline = false,
  fieldProps = {},
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  component: Component = InputComponent,
  className,
  disabled = false,
  labelClassName,
  validationProps,
  prefix,
  ...rest
}) => (
  <Field
    name={name}
    disabled={disabled}
    type={type}
    validate={required && Required}
    {...fieldProps}
  >
    {({
      input: { onChange: inputOnChange, onFocus: inputOnFocus, onBlur: inputOnBlur, ...input },
      meta,
    }) => {
      const isSelect = Component === SelectComponent;

      const componentProps: any = {
        className: cx([
          'outline-none border-grey-light',
          isError(meta) && 'border-red',
          !isSelect && (disabled ? 'bg-grey-lightest  text-grey' : 'bg-white'),
          className,
        ]),
        disabled,
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          inputOnFocus(e);
          onFocus(e);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          inputOnBlur(e);
          onBlur(e);
        },
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          inputOnChange(e);
          onChange(e);
        },
        prefix,
        ...(isSelect && {
          colorClassName: disabled ? 'bg-grey-lightest text-grey' : 'bg-white text-black',
        }),
        ...rest,
        ...input,
      };

      if (label) {
        componentProps.label = {
          children: LabelComponent ? (
            <LabelComponent required={required} error={isError(meta)}>
              {label}
            </LabelComponent>
          ) : (
            label
          ),
          className: cx(
            labelClassName,
            'text-micro font-medium',
            isError(meta) ? 'text-red' : 'text-grey-dark'
          ),
        };
      }
      return (
        <FieldError
          data-testid={`field-error-${name}`}
          inline={inline}
          meta={meta}
          validationProps={validationProps}
        >
          <Component {...componentProps} />
        </FieldError>
      );
    }}
  </Field>
);

export default InputWrap;
